import { apiUrl } from '../lib/api-base'
import { clearAuthSession, getAuthItem } from '../shared/authStorage'

export const IB_TOKEN_KEY = 'pp_ib_token'
export const IB_USER_KEY = 'pp_ib_user'

export type IbSubadmin = {
  id: number
  code: string
  email: string
  displayName: string
  phone?: string
  country?: string
  status: string
  profitSharePercent: number
  lossSharePercent: number
  depositCommissionPercent?: number
  commissionHoldDays?: number
  minimumReserve: number
  balance: number
  withdrawable: number
  totalCommission?: number
  withdrawableCommission?: number
  pendingCommission?: number
  referralLink: string
  mt5CommentTag?: string
  createdAt?: string
  clientCount?: number
}

export type IbApplication = {
  id: number
  email: string
  displayName: string
  phone?: string
  country?: string
  notes?: string
  status: string
  createdAt?: string
  reviewNote?: string
}

type ApiError = { code?: string; message?: string }

export class IbApiError extends Error {
  code?: string
  status?: number

  constructor(message: string, opts?: { code?: string; status?: number }) {
    super(message)
    this.name = 'IbApiError'
    this.code = opts?.code
    this.status = opts?.status
  }
}

async function request<T>(path: string, init: RequestInit = {}, auth = false): Promise<T> {
  const headers = new Headers(init.headers || {})
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json')
  }
  if (auth) {
    const token = getAuthItem(IB_TOKEN_KEY)
    if (token) headers.set('Authorization', `Bearer ${token}`)
  }

  let res: Response
  try {
    res = await fetch(apiUrl(path), { ...init, headers })
  } catch {
    throw new IbApiError('Unable to reach the server', { code: 'NETWORK' })
  }

  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err = (data as { error?: ApiError }).error
    const code = (err?.code || '').toUpperCase()
    const sessionDead =
      code === 'UNAUTHORIZED' ||
      code === 'UNAUTHENTICATED' ||
      code === 'DISABLED' ||
      code === 'FORBIDDEN' ||
      (!code && (res.status === 401 || res.status === 403))
    if (auth && (res.status === 401 || res.status === 403) && sessionDead) {
      clearAuthSession(IB_TOKEN_KEY, IB_USER_KEY)
    }
    const fallback =
      res.status === 429
        ? 'Too many requests. Please try again shortly.'
        : `Request failed (${res.status})`
    throw new IbApiError(err?.message || fallback, {
      code: err?.code || (res.status === 429 ? 'RATE_LIMITED' : undefined),
      status: res.status,
    })
  }
  return data as T
}

export const ibApi = {
  applyInfo() {
    return request<{ success: boolean; applyLink: string; message?: string }>('/api/ib/apply-info')
  },

  apply(payload: {
    email: string
    password: string
    displayName: string
    phone?: string
    country?: string
    notes?: string
  }) {
    return request<{ success: boolean; application: { id: number; status: string } }>('/api/ib/apply', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  login(email: string, password: string) {
    return request<{ success: boolean; accessToken: string; subadmin: IbSubadmin }>('/api/ib/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    })
  },

  me() {
    return request<{ success: boolean; subadmin: IbSubadmin }>('/api/ib/me', {}, true)
  },

  changePassword(currentPassword: string, newPassword: string) {
    return request<{ success: boolean; message?: string }>(
      '/api/ib/change-password',
      {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      },
      true,
    )
  },

  forgotPassword(email: string) {
    return request<{ success: boolean; message?: string }>('/api/ib/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    })
  },

  resetPassword(token: string, newPassword: string) {
    return request<{ success: boolean; message?: string }>('/api/ib/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    })
  },

  dashboard() {
    return request<{
      success: boolean
      subadmin: IbSubadmin
      stats: {
        clientCount: number
        balance: number
        minimumReserve: number
        withdrawable: number
        totalCommission: number
        withdrawableCommission: number
        pendingCommission: number
      }
      referralLink: string
      recentLedger: Array<Record<string, unknown>>
    }>('/api/ib/dashboard', {}, true)
  },

  clients() {
    return request<{
      success: boolean
      clients: Array<{
        id: number
        email?: string
        phone?: string
        firstName?: string
        lastName?: string
        status?: string
        createdAt?: string
        accounts: Array<Record<string, unknown>>
      }>
    }>('/api/ib/clients', {}, true)
  },

  netPositions() {
    return request<{
      success: boolean
      symbols: Array<{
        symbol: string
        buyLots: number
        sellLots: number
        buyAvg: number
        sellAvg: number
        netLots: number
      }>
      errors?: number
    }>('/api/ib/net-positions', {}, true)
  },

  wallet() {
    return request<{
      success: boolean
      subadmin: IbSubadmin
      ledger: Array<Record<string, unknown>>
      requests: Array<Record<string, unknown>>
    }>('/api/ib/wallet', {}, true)
  },

  walletRequest(type: 'DEPOSIT' | 'WITHDRAWAL', amount: number, note?: string) {
    return request<{ success: boolean; request: Record<string, unknown> }>('/api/ib/wallet/request', {
      method: 'POST',
      body: JSON.stringify({ type, amount, note: note || '' }),
    }, true)
  },

  notifications(limit = 30) {
    return request<{
      success: boolean
      unreadCount: number
      notifications: Array<{
        id: number
        title: string
        body: string
        ctaLabel?: string
        ctaPath?: string
        read: boolean
        createdAt?: string
      }>
    }>(`/api/ib/notifications?limit=${limit}`, {}, true)
  },

  notificationsRead(id?: number) {
    return request<{ success: boolean; updated: number; unreadCount: number }>(
      '/api/ib/notifications/read',
      {
        method: 'POST',
        body: JSON.stringify(id != null ? { id } : {}),
      },
      true,
    )
  },
}
